<template>
  <div class="days-reservations">
    <div class="filters">
      <input
        type="text"
        v-model="filters.search"
        :placeholder="$t('dashboardReservations.searchClients')" />

      <span
        v-if="servicesList.length"
        class="separator"></span>

      <select
        v-if="servicesList.length"
        v-model="filters.service">
        <option key="services-list-group-00" value="">{{ $t('common.ui.allServices') }}</option>
        <template
          v-for="(servicesCategory, index) of servicesCategories"
          v-if="servicesInCategory(servicesCategory.id).length">
          <option :key="'services-list-group-' + index" :value="'c' + servicesCategory.id">
            {{ servicesCategory.name }}
          </option>
          <option
            v-for="(service, subindex) of servicesInCategory(servicesCategory.id)"
            :value="service.id"
            :key="'services-list-group-' + index + '-' + subindex">
            &mdash; {{ service.name }}
          </option>
        </template>
      </select>

      <span class="separator"></span>

      <btn
        @click.native="clearFilters"
        type="danger with-icon"
        iconName="cross"
        iconSize="xxxs"
        iconColor="color-light"
        :disabled="!filterIsActive">
        {{ $t('common.ui.clearFilters') }}
      </btn>
    </div>

    <div
      v-if="reservationsLoaded && reservations.length"
      class="days-reservations-block">
      <div class="days-reservations-header">
        <h3>{{ $t('dashboardReservations.daysReservationsStarts') }}</h3>
      </div>

      <div :class="{ 'days-reservations-list': true }">
        <reservation
          v-for="(reservationData, index) in filteredReservationsStarts"
          :reservationData="reservationData"
          :key="'trl-' + index"
          :isReservation="true"
          :isBlock="isMobile || isTablet"
          :reservationHoursFirst="true"
          :daysMode="true" />
      </div>

      <div
        v-if="!filteredReservationsStarts.length"
        class="days-filtered-reservations-empty-state">
        <template v-if="!filterIsActive">{{ $t('dashboardReservations.noStartingReservations') }}</template>
        <template v-if="filterIsActive">{{ $t('dashboardReservations.noStartingReservationsForGivenFilters') }}</template>
      </div>
    </div>

    <div
      v-if="!reservationsLoaded"
      class="days-reservations-empty-state">
      {{ $t('dashboardReservations.loadingReservations') }}
    </div>

    <message
      v-if="lastUpdate && lastDataDownload && lastUpdate > lastDataDownload"
      type="info">
      {{ $t('dashboard.updateRequired') }}
      <a
        v-if="!loadingData"
        href="#"
        @click.prevent="loadReservations">
        {{ $t('dashboard.updateData') }}
      </a>
      <a
        v-else
        href="#"
        @click.prevent="false">
        {{ $t('dashboard.updatingData') }}
      </a>
    </message>

    <div
      v-if="reservations.length"
      class="days-reservations-block">
      <div class="days-reservations-header">
        <h3>{{ $t('dashboardReservations.daysReservationsEnds') }}</h3>
      </div>

      <div :class="{ 'days-reservations-list': true }">
        <reservation
          v-for="(reservationData, index) in filteredReservationsEnds"
          :reservationData="reservationData"
          :key="'trl-' + index"
          :isReservation="true"
          :isBlock="isMobile || isTablet"
          :reservationHoursFirst="true"
          :daysMode="true" />
      </div>

      <div
        v-if="!filteredReservationsEnds.length"
        class="days-filtered-reservations-empty-state">
        <template v-if="!filterIsActive">{{ $t('dashboardReservations.noEndingReservations') }}</template>
        <template v-if="filterIsActive">{{ $t('dashboardReservations.noEndingReservationsForGivenFilters') }}</template>
      </div>
    </div>

    <div
      v-if="!reservationsLoaded"
      class="days-reservations-empty-state">
      {{ $t('dashboardReservations.loadingReservations') }}
    </div>

    <div
      v-if="!reservations.length && reservationsLoaded"
      class="days-reservations-empty-state">
      {{ $t('dashboardReservations.noUpcomingReservationsForDays') }}
    </div>
  </div>
</template>

<script>
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Reservation from './Reservation';
import Responsive from './../mixins/Responsive';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'dashboard-days-reservations',
  mixins: [
    Configuration,
    Responsive,
    WordVariations
  ],
  props: [
    'services',
    'servicesCategories',
    'servicesLoaded'
  ],
  components: {
    'reservation': Reservation
  },
  data () {
    return {
      filters: {
        service: '',
        search: ''
      },
      reservations: [],
      reservationsLoaded: false,
      lastUpdate: null,
      lastDataDownload: null,
      loadingData: false
    }
  },
  computed: {
    filteredReservationsStarts () {
      let todaysDate = new Date();
      todaysDate = [todaysDate.getFullYear(), todaysDate.getMonth() + 1, todaysDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');

      return this.reservations.filter((reservationData) => {
        if (reservationData.start_at.split(' ')[0] !== todaysDate) {
          return false;
        }

        if (this.filters.service !== '' && reservationData.service.id !== this.filters.service) {
          return false;
        }

        if (
          this.filters.search !== '' &&
          reservationData.user_name.toLowerCase().indexOf(this.filters.search.toLowerCase()) === -1 &&
          reservationData.confirm_email.toLowerCase().indexOf(this.filters.search.toLowerCase()) === -1
        ) {
          return false;
        }

        return true;
      });
    },
    filteredReservationsEnds () {
      let todaysDate = new Date();
      todaysDate = [todaysDate.getFullYear(), todaysDate.getMonth() + 1, todaysDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');

      return this.reservations.filter((reservationData) => {
        if (reservationData.end_at.split(' ')[0] !== todaysDate) {
          return false;
        }

        if (this.filters.service !== '' && reservationData.service.id !== this.filters.service) {
          return false;
        }

        if (
          this.filters.search !== '' &&
          reservationData.user_name.toLowerCase().indexOf(this.filters.search.toLowerCase()) === -1 &&
          reservationData.confirm_email.toLowerCase().indexOf(this.filters.search.toLowerCase()) === -1
        ) {
          return false;
        }

        return true;
      });
    },
    filterIsActive () {
      return this.filters.service !== '' || this.filters.search !== '';
    },
    servicesList () {
      return this.services.filter(service => service.available_in_dates === 3).map(service => ({
        id: service.id,
        name: service.name,
        category_id: service.category_id
      }));
    }
  },
  mounted () {
    this.$bus.$on('dashboard-reservations-list-reload-needed', this.loadReservations);
    this.$bus.$on('dashboard-reservations-list-tab-days-needs-update', this.updateRequired);
    this.loadReservations();
  },
  methods: {
    clearFilters () {
      this.filters.service = '';
      this.filters.search = '';
    },
    loadReservations () {
      this.loadingData = true;
      let todaysDate = new Date();
      todaysDate = [todaysDate.getFullYear(), todaysDate.getMonth() + 1, todaysDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      this.$http.get('/panel/calendar/get-daily-calendar?date_from=' + todaysDate + '&date_to=' + todaysDate + '&show_all_data=1').then(response => {
        let loadedReservations = response.data.days[0].reservations;
        let foundedServices = Object.keys(loadedReservations);
        let allReservations = [];

        for (let i = 0; i < foundedServices.length; i++) {
          allReservations = allReservations.concat(loadedReservations[foundedServices[i]]);
        }

        this.reservations = allReservations;
        this.reservationsLoaded = true;
        this.lastDataDownload = +new Date();
        this.loadingData = false;
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/calendar/get-daily-calendar?date_from=' + todaysDate + '&date_to=' + todaysDate + '&show_all_data=1', this.$router);
        this.reservationsLoaded = true;
        this.loadingData = false;
      });
    },
    servicesInCategory (categoryID) {
      return this.servicesList.filter(service => service.category_id === categoryID);
    },
    updateRequired () {
      this.lastUpdate = +new Date();
    }
  },
  beforeDestroy () {
    this.$bus.$off('dashboard-reservations-list-reload-needed', this.loadReservations);
    this.$bus.$off('dashboard-reservations-list-tab-days-needs-update', this.updateRequired);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.days-reservations {
  &-block {
    background: $color-light;
    border: 1px solid $color-border;
    padding: 2.5rem;

    & + .days-reservations-block {
      margin-top: 2rem;
    }
  }

  &-header {
    align-items: center;
    display: flex;
  }

  &-switcher {
    align-items: center;
    display: flex;
    margin-left: auto;

    .switcher {
      margin-left: 1rem;
    }
  }

  &-empty-state {
    color: $color-text-light;
    text-align: center;
  }

  h3 {
    font-size: 2.4rem;
    font-weight: 400;
    line-height: 1;
    margin: 0;
  }

  &-list {
    display: flex;
    flex-wrap: wrap;
    margin: 2.6rem -2.6rem -2.6rem -2.6rem;
    width: auto;

    & + .days-filtered-reservations-empty-state {
      color: $color-text-light;
      display: block;
      padding: 2rem 0 0 0;
      text-align: center;
    }
  }

  &-past-counter {
    color: $color-text-light;
    margin-left: 2rem;
  }
}

.filters {
  display: flex;
  margin-bottom: 1rem;
  margin-top: -4.3rem;
  padding: 2rem 0;
  width: 100%;

  .button {
    width: 16rem;
  }

  select {
    height: 3.6rem;
    line-height: 3.6rem;
    min-width: 24rem;
    width: 24rem;
  }

  input {
    background: $color-light url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill-rule="evenodd" fill="%237F8FA4" d="M382.217,267.717a1,1,0,0,1-1.414,0l-3.15-3.15a7,7,0,1,1,1.415-1.414l3.149,3.15A1,1,0,0,1,382.217,267.717Zm-5.2-12.271a5,5,0,1,0,0,7.071A5,5,0,0,0,377.017,255.446Z" transform="translate(-366.5 -252)"/></svg>');
    background-repeat: no-repeat;
    background-position: calc(100% - 1.2rem) center;
    border: 1px solid $color-border;
    border-radius: $border-radius-small;
    height: 3.6rem;
    padding: 0 1rem;
    width: 100%;
  }

  .separator {
    display: block;
    height: 3.2rem;
    position: relative;
    min-width: 2.5rem;
    width: 2.5rem;

    &:before {
      background: $color-text-lightest;
      content: "";
      height: 24px;
      left: 12px;
      position: absolute;
      top: 6px;
      width: 1px;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all .25s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: scale(.5);
}

@media (max-width: 1040px) {
  .days-reservations {
    .filters {
      background: $color-bg-medium;
      flex-wrap: wrap;
      margin: -2.6rem -1.5rem 2rem -1.5rem;
      padding: 1rem 1.5rem 2rem 1.5rem;
      width: calc(100% + 3rem);

      & > .separator {
        display: none;
      }

      & > select,
      & > input,
      & > .button {
        margin-bottom: 1rem;
        width: 100%;
      }

      & > .button {
        margin: 0;
      }
    }

    .days-reservations-header {
      flex-wrap: wrap;

      h3 {
        width: 100%;
      }

      .days-reservations-past-counter {
        margin: 0;
        width: 50%;

        & + .days-reservations-switcher {
          width: 50%;
        }
      }

      .days-reservations-switcher {
        justify-content: flex-end;
        margin: 0;
        width: 100%;
      }
    }

    .days-reservations-list {
      margin: 0 -1.25rem 1rem -1.25rem;

      .reservation {
        margin: 2.5rem 1.25rem 0 1.25rem;
        max-width: calc(50% - 2.5rem);
      }
    }
  }
}

@media (max-width: 720px) {
  .days-reservations {
    .days-reservations-header {
      h3,
      .days-reservations-past-counter,
      .days-reservations-switcher {
        justify-content: flex-start;
        padding: .75rem 0;
        width: 100%!important;
      }

      h3 {
        padding-top: 0;
      }
    }
  }
}

@media (max-width: 680px) {
  .days-reservations {
    .days-reservations-block {
      padding: 2.5rem 1.5rem;
    }

    .days-reservations-day-label {
      font-size: 1.5rem;

      .counter {
        top: 0;
      }
    }

    .days-reservations-day {
      margin: 0 -1.5rem;
      padding: 0 1.5rem;
    }

    .days-reservations-list {
      margin: 0 0 1rem 0;

      .reservation {
        margin: 2.5rem 0 0 0;
        max-width: 100%;
      }
    }
  }
}

@media (max-width: 480px) {
  .days-reservations {
    .days-reservations-header {
      .days-reservations-past-counter,
      .days-reservations-switcher {
        font-size: 1.3rem;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/DashboardDaysReservations.vue